<script lang="ts">
import { confirmed, email, required } from '@vee-validate/rules';
import { Field } from 'vee-validate';
import { computed, defineComponent, ref } from 'vue';

import FieldErrorIcon from '@/components/base/assets/FieldErrorIcon.vue';
import FloatingLabel from '@/components/base/form/FloatingLabel.vue';
import InlineError from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';

export default defineComponent({
  name: 'GiftRecipientForm',
  props: {
    email: { required: true, type: String },
    name: { required: true, type: String },
    showName: { required: false, type: Boolean, default: true },
  },
  components: {
    Field,
    FieldErrorIcon,
    FloatingLabel,
    InlineError,
    // eslint-disable-next-line vue/no-reserved-component-names
    Input,
  },
  emits: ['update:email', 'update:name'],
  setup(props, { emit }) {
    const emailConfirmation = ref<string | undefined>(props.email);

    const recipientEmail = computed({
      get: () => props.email,
      set: (val) => emit('update:email', val),
    });

    const recipientName = computed({
      get: () => props.name,
      set: (val) => emit('update:name', val),
    });

    const { errorMessages, validatorFailed } = useForm();
    const schema = computed(() => {
      const rules: {
        confirmEmail: FlexibleMessageValidator<string>;
        giftRecipientEmail: FlexibleMessageValidator<string>;
        giftRecipientName?: FlexibleMessageValidator<string>;
      } = {
        confirmEmail: (value) => {
          const { recipientEmailConfirmation: confirmationMessages } = errorMessages;
          if (!required(value)) return confirmationMessages.required;
          if (!email(value)) return confirmationMessages.email;
          if (!confirmed(value, [props.email ?? ''])) return confirmationMessages.confirmed;
          return true;
        },
        giftRecipientEmail: (value) => {
          if (!required(value)) return errorMessages.recipientEmail.required;
          if (!email(value)) return errorMessages.recipientEmail.email;
          return true;
        },
      };

      if (props.showName) {
        rules.giftRecipientName = (value) =>
          required(value) || errorMessages.recipientName.required;
      }

      return rules;
    });

    return {
      emailConfirmation,
      recipientEmail,
      recipientName,
      schema,
      validatorFailed,
    };
  },
});
</script>

<template>
  <div>
    <div class="font-bold">Gift Certificate Recipient</div>
    <Field
      v-if="showName"
      v-model="recipientName"
      name="giftRecipientName"
      :rules="schema.giftRecipientName"
      v-slot="{ errors, field, meta }"
    >
      <div class="relative w-full mt-3">
        <Input
          v-bind="field"
          type="text"
          id="gift-recipient-name"
          placeholder="full name"
          aria-label="full name"
          :invalid="validatorFailed(meta)"
          data-test="gift-recipient-name"
        />
        <FloatingLabel class="left-0" data-test="full-name-label" :invalid="validatorFailed(meta)">
          Recipient Name*
        </FloatingLabel>
        <FieldErrorIcon class="z-20" :showErrorIcon="validatorFailed(meta)" />
        <InlineError
          :errors="errors"
          fieldName="Recipient Email"
          :invalid="validatorFailed(meta)"
          data-test="email-validation-error"
        />
      </div>
    </Field>
    <Field
      v-model="recipientEmail"
      name="giftRecipientEmail"
      :rules="schema.giftRecipientEmail"
      v-slot="{ errors, field, meta }"
    >
      <div class="relative w-full" :class="[showName ? 'mt-6 md:mt-8' : 'mt-3']">
        <Input
          v-bind="field"
          type="email"
          id="gift-recipient-email"
          placeholder="email"
          aria-label="email"
          ref="email"
          :invalid="validatorFailed(meta)"
          data-test="gift-recipient-email"
        />
        <FloatingLabel class="left-0" data-test="email-label" :invalid="validatorFailed(meta)">
          Recipient Email*
        </FloatingLabel>
        <FieldErrorIcon class="z-20" :showErrorIcon="validatorFailed(meta)" />
        <InlineError
          :errors="errors"
          fieldName="Recipient Email"
          :invalid="validatorFailed(meta)"
          data-test="email-validation-error"
        />
      </div>
    </Field>
    <Field
      v-model="emailConfirmation"
      name="confirmEmail"
      :rules="schema.confirmEmail"
      v-slot="{ errors, field, meta }"
    >
      <div class="relative w-full mt-6 md:mt-8">
        <Input
          v-bind="field"
          type="email"
          id="gift-recipient-email-confirmation"
          placeholder="email confirmation"
          aria-label="email-confirmation"
          :invalid="validatorFailed(meta)"
          data-test="gift-recipient-email-confirmation"
        />
        <FloatingLabel
          class="left-0"
          data-test="email-confirmation-label"
          :invalid="validatorFailed(meta)"
        >
          Confirm Recipient Email*
        </FloatingLabel>
        <FieldErrorIcon class="z-20" :showErrorIcon="validatorFailed(meta)" />
        <InlineError
          :errors="errors"
          fieldName="Recipient Confirmation Email"
          :invalid="validatorFailed(meta)"
          data-test="email-confirmation-validation-error"
        />
      </div>
    </Field>
  </div>
</template>
